import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, ChevronDown, ExternalLink } from 'lucide-react'

/**
 * /no-linked-mall — 캠퍼스 SSO 진입 시 link 0개 사용자에게 보여줄 안내.
 *
 * 캠퍼스 사용자가 "솔루션 ↗"을 눌렀는데 연결된 솔루션 매장이 없는 경우.
 * link 0개는 세 가지 상태를 포함하며 campus_user_id만으로는 구분 불가:
 *  - 앱 미설치(신규 캠퍼스 계정) / 카페24에서 앱 삭제 후(uninstall 웹훅이 link 해제)
 *    → 앱스토어에서 (재)설치해야 함. 설치 시 캠퍼스 로그인으로 자동 연결됨. (대다수)
 *  - 앱은 설치됐으나 미연결(레거시: 캠퍼스 강제 로그인 이전 설치)
 *    → 대시보드에서 연결. (소수)
 * 따라서 "앱스토어 설치"를 1차 경로로, "이미 설치된 경우 연결"을 접이식 보조로 안내한다.
 */
export const Route = createFileRoute('/no-linked-mall')({
  component: NoLinkedMallPage,
})

/** 자사몰 사관학교 솔루션 카페24 앱스토어 리스팅 — 설치/재설치 진입점 (1차 경로). */
const CAFE24_APP_STORE_URL = 'https://store.cafe24.com/kr/apps/31847'

/** 카페24 관리자 로그인 — 이미 앱이 설치된(레거시) 사용자의 대시보드 진입 경로. */
const CAFE24_ADMIN_LOGIN_URL = 'https://eclogin.cafe24.com/'

/** 캠퍼스 홈 — 환경변수 미설정 시 dev 도메인 fallback. */
const CAMPUS_URL =
  import.meta.env.VITE_CAMPUS_URL || 'https://dev.1bpluschool.com'

/** 가이드 스크린샷 경로 (`dashboard/public/guide/` 에 배치). */
const GUIDE_IMAGES = {
  step1: '/guide/step1-cafe24-app-menu.png',
  step2: '/guide/step2-solution-profile-menu.png',
  step3: '/guide/step3-link-card.png',
} as const

interface StepProps {
  number: number
  imageSrc: string
  imageAlt: string
  description: React.ReactNode
}

function Step({ number, imageSrc, imageAlt, description }: StepProps) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
        {number}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-on-surface leading-relaxed mb-2">
          {description}
        </p>
        <div className="bg-surface rounded-lg overflow-hidden border border-border">
          {/* placeholder 자산 미배치 시 깨진 아이콘 대신 컨테이너를 숨겨 레이아웃 보호. */}
          <img
            src={imageSrc}
            alt={imageAlt}
            className="w-full block"
            loading="lazy"
            onError={(e) => {
              const el = e.currentTarget
              const parent = el.parentElement
              if (parent) parent.style.display = 'none'
            }}
          />
        </div>
      </div>
    </div>
  )
}

function NoLinkedMallPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface px-4 py-8">
      <div className="max-w-lg w-full bg-surface-lowest rounded-2xl border border-border p-6 sm:p-8">
        {/* 헤더 */}
        <div className="text-center mb-6">
          <img
            src="/images/onebplus_logo.svg"
            alt="자사몰 사관학교 솔루션"
            className="mx-auto mb-4 h-10"
          />
          <h1 className="text-lg font-bold text-on-surface mb-2">
            자사몰 사관학교 솔루션 연결하기
          </h1>
          <p className="text-sm text-on-surface-variant leading-relaxed">
            캠퍼스 계정에 연결된 솔루션이 없어요.
            <br />
            카페24에 솔루션을 설치하면 캠퍼스 계정과 자동으로 연결돼요.
          </p>
        </div>

        {/* 1차 경로 — 앱스토어 설치 (미설치/삭제 후 모두 커버) */}
        <a
          href={CAFE24_APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full rounded-lg bg-primary text-on-primary py-3 text-sm font-medium hover:opacity-90 transition-opacity"
        >
          카페24 앱스토어에서 설치하기
          <ExternalLink className="h-3.5 w-3.5" />
        </a>

        {/* 캠퍼스로 돌아가기 — 자사몰 사관학교 캠퍼스 BI 메인 컬러(#ff5757) outline. */}
        <a
          href={CAMPUS_URL}
          className="mt-2 flex items-center justify-center gap-2 w-full rounded-lg border border-[#ff5757] bg-surface-lowest text-[#ff5757] py-2.5 text-sm font-medium hover:bg-[#ff5757]/10 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          자사몰 사관학교 캠퍼스로 돌아가기
        </a>

        {/* 보조 경로 (레거시) — 앱이 이미 설치된 경우 대시보드에서 연결.
            기본은 접혀 있어 1차 경로(설치)에 집중되게 한다. */}
        <details className="group mt-6">
          <summary className="flex cursor-pointer list-none items-center justify-center gap-1 text-sm text-on-surface-variant transition-colors hover:text-on-surface">
            이미 앱을 설치하셨나요?
            <ChevronDown className="h-4 w-4 text-outline-variant transition-transform group-open:rotate-180" />
          </summary>
          <div className="mt-4 border-t border-border pt-4">
            <p className="text-sm text-on-surface-variant leading-relaxed mb-4">
              앱이 이미 설치돼 있다면 아래 순서로 대시보드에서 연결해주세요.
            </p>
            <div className="space-y-4 mb-4">
              <Step
                number={1}
                imageSrc={GUIDE_IMAGES.step1}
                imageAlt="카페24 관리자의 앱 메뉴에서 자사몰 사관학교 앱 관리하기 버튼"
                description={
                  <>
                    카페24 관리자 → <span className="font-semibold">[앱]</span>{' '}
                    메뉴 →{' '}
                    <span className="font-semibold">
                      '자사몰 사관학교 앱 관리하기'
                    </span>{' '}
                    클릭하면 솔루션 대시보드로 진입해요.
                  </>
                }
              />
              <Step
                number={2}
                imageSrc={GUIDE_IMAGES.step2}
                imageAlt="솔루션 대시보드 우상단 프로필 아이콘"
                description={
                  <>
                    대시보드{' '}
                    <span className="font-semibold">우상단 프로필 아이콘</span>{' '}
                    → 계정 메뉴를 열어주세요.
                  </>
                }
              />
              <Step
                number={3}
                imageSrc={GUIDE_IMAGES.step3}
                imageAlt="드롭다운 메뉴 안의 자사몰 사관학교 캠퍼스 항목과 연결하기 버튼"
                description={
                  <>
                    메뉴 하단{' '}
                    <span className="font-semibold">
                      '자사몰 사관학교 캠퍼스'
                    </span>{' '}
                    →{' '}
                    <span className="font-semibold text-primary">
                      [연결하기]
                    </span>{' '}
                    클릭하면 완료돼요.
                  </>
                }
              />
            </div>
            <a
              href={CAFE24_ADMIN_LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full rounded-lg border border-border bg-surface py-2.5 text-sm font-medium text-on-surface-variant transition-colors hover:bg-surface-low"
            >
              카페24 관리자 페이지 열기
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </details>
      </div>
    </div>
  )
}
